/**
 * useVariantUnlocks — Shared hook for Djay variant unlock conditions.
 *
 * DDD co-locatie: mascot domain.
 * Single source of truth for which variants are unlocked, used by
 * FloatingDjay, Sidebar, MobileNav, and CollectionPage.
 *
 * Audit 2026: nu gebaseerd op `useLearnerContext` (DRY-fix) en
 * `DJAY_THRESHOLDS` (centrale magic-numbers). Zie
 * docs/audits/MASCOT_DOMAIN_AUDIT_2026.md §7.
 */

import { useMemo } from 'react'
import { DjayVariant } from './DjayAvatar'
import { useAppStore } from '../app/hooks/useAppStore'
import { useLearnerContext } from './useLearnerContext'
import { DJAY_THRESHOLDS } from './config'
import { MODULES } from '../content'
import type { UnlockContext } from './unlock/unlockContext'

export type MascotRarity =
  | 'common'
  | 'uncommon'
  | 'rare'
  | 'legendary'
  | 'secret'

export interface VariantProgress {
  current: number
  max: number
  label: string
}

export interface VariantUnlockInfo {
  id: DjayVariant
  name: string
  unlocked: boolean
  /** Human-readable unlock condition (Dutch) */
  condition: string
  rarity: MascotRarity
  description: string
  progress: VariantProgress
}

// The variant-unlock context now uses the shared, canonical `UnlockContext`
// from `unlock/unlockContext.ts` (audit §9.4) instead of a private parallel
// shape. `UnlockContext` is a superset of `AchievementCheckData`, so the
// variant grid and the achievement cards read one shared facts-set.

interface VariantDefinition {
  id: DjayVariant
  name: string
  /** Name shown when secret is not yet discovered */
  hiddenName?: string
  condition: string
  rarity: MascotRarity
  description: string
  /** Description shown when secret is not yet discovered */
  hiddenDescription?: string
  check: (ctx: UnlockContext) => boolean
  getProgress: (ctx: UnlockContext) => VariantProgress
}

const VARIANT_DEFINITIONS: VariantDefinition[] = [
  {
    id: 'default',
    name: 'Standaard Djay',
    condition: 'Altijd beschikbaar',
    rarity: 'common',
    description: 'Altijd beschikbaar',
    check: () => true,
    getProgress: () => ({ current: 1, max: 1, label: 'Ontgrendeld' }),
  },
  {
    id: 'pixel',
    name: 'DJ Pixel',
    condition: `${DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED} vragen beantwoord`,
    rarity: 'uncommon',
    description: 'Honderdtal bereikt',
    check: (ctx) =>
      ctx.totalQuestionsAnswered >= DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED,
    getProgress: (ctx) => ({
      current: Math.min(
        ctx.totalQuestionsAnswered,
        DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED,
      ),
      max: DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED,
      label: 'vragen',
    }),
  },
  {
    id: 'blaze',
    name: 'DJ Blaze',
    condition: `${DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS} weken streak`,
    rarity: 'rare',
    description: `${DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS} volle weken achter elkaar`,
    check: (ctx) =>
      ctx.consecutiveFullWeeks >= DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS,
    getProgress: (ctx) => ({
      current: Math.min(
        ctx.consecutiveFullWeeks,
        DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS,
      ),
      max: DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS,
      label: 'weken',
    }),
  },
  {
    id: 'frost',
    name: 'DJ Frost',
    condition: `${DJAY_THRESHOLDS.FROST_MODULES_MASTERED} vakken beheerst`,
    rarity: 'rare',
    description: `${DJAY_THRESHOLDS.FROST_MODULES_MASTERED} vakken afgerond`,
    check: (ctx) => ctx.masteredCount >= DJAY_THRESHOLDS.FROST_MODULES_MASTERED,
    getProgress: (ctx) => ({
      current: Math.min(
        ctx.masteredCount,
        DJAY_THRESHOLDS.FROST_MODULES_MASTERED,
      ),
      max: DJAY_THRESHOLDS.FROST_MODULES_MASTERED,
      label: 'vakken',
    }),
  },
  {
    id: 'royal',
    name: 'DJ Royal',
    condition: 'Alle vakken beheerst',
    rarity: 'legendary',
    description: 'Alle vakken afgerond',
    check: (ctx) => ctx.masteredCount >= ctx.totalModules,
    getProgress: (ctx) => ({
      current: Math.min(ctx.masteredCount, ctx.totalModules),
      max: ctx.totalModules,
      label: 'vakken',
    }),
  },
  {
    id: 'cosmic',
    name: 'DJ Cosmic',
    condition: `Level ${DJAY_THRESHOLDS.COSMIC_LEVEL} bereikt`,
    rarity: 'legendary',
    description: `Level ${DJAY_THRESHOLDS.COSMIC_LEVEL} bereikt`,
    check: (ctx) => ctx.level >= DJAY_THRESHOLDS.COSMIC_LEVEL,
    getProgress: (ctx) => ({
      current: Math.min(ctx.level, DJAY_THRESHOLDS.COSMIC_LEVEL),
      max: DJAY_THRESHOLDS.COSMIC_LEVEL,
      label: 'levels',
    }),
  },
  {
    id: 'shadow',
    name: 'DJ Shadow',
    condition: `${DJAY_THRESHOLDS.SHADOW_XP.toLocaleString('nl-NL')} XP verzameld`,
    rarity: 'legendary',
    description: `${DJAY_THRESHOLDS.SHADOW_XP.toLocaleString('nl-NL')} XP behaald`,
    check: (ctx) => ctx.xp >= DJAY_THRESHOLDS.SHADOW_XP,
    getProgress: (ctx) => ({
      current: Math.min(ctx.xp, DJAY_THRESHOLDS.SHADOW_XP),
      max: DJAY_THRESHOLDS.SHADOW_XP,
      label: 'XP',
    }),
  },
  {
    id: 'goud',
    name: 'DJ Goud',
    hiddenName: '???',
    condition: 'Geheim ontdekt',
    rarity: 'secret',
    description: 'Geheim ontdekt!',
    hiddenDescription: 'Geheim! Ontdek het zelf...',
    check: (ctx) => ctx.hasDiscoveredLongPress,
    getProgress: (ctx) => ({
      current: ctx.hasDiscoveredLongPress ? 1 : 0,
      max: 1,
      label: 'Geheim',
    }),
  },
  {
    id: 'rainbow',
    name: 'DJ Regenboog',
    condition: 'Verzamel alle 6 leer-mascottes',
    rarity: 'legendary',
    description: 'Alle 6 leer-mascottes verzameld',
    check: (ctx) =>
      ctx.totalQuestionsAnswered >= DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED &&
      ctx.consecutiveFullWeeks >=
        DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS &&
      ctx.masteredCount >= DJAY_THRESHOLDS.FROST_MODULES_MASTERED &&
      ctx.masteredCount >= ctx.totalModules &&
      ctx.level >= DJAY_THRESHOLDS.COSMIC_LEVEL &&
      ctx.xp >= DJAY_THRESHOLDS.SHADOW_XP,
    getProgress: (ctx) => {
      const unlocked = [
        ctx.totalQuestionsAnswered >= DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED,
        ctx.consecutiveFullWeeks >=
          DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS,
        ctx.masteredCount >= DJAY_THRESHOLDS.FROST_MODULES_MASTERED,
        ctx.masteredCount >= ctx.totalModules,
        ctx.level >= DJAY_THRESHOLDS.COSMIC_LEVEL,
        ctx.xp >= DJAY_THRESHOLDS.SHADOW_XP,
      ].filter(Boolean).length
      return {
        current: unlocked,
        max: 6,
        label: 'mascottes',
      }
    },
  },
]

/**
 * Returns all variant unlock info based on current learner state.
 * Pass `hasDiscoveredLongPress` from the caller since it requires
 * an async dataLayer lookup that the caller manages.
 */
export function useVariantUnlocks(hasDiscoveredLongPress: boolean): {
  variants: VariantUnlockInfo[]
  unlockedCount: number
} {
  const { state } = useAppStore()
  const { level, xp, masteredCount, totalQuestionsAnswered } =
    useLearnerContext()
  const consecutiveFullWeeks = state?.dailyStreak?.consecutiveFullWeeks || 0
  // Remaining achievement-check fields, sourced from the same store slices as
  // `useAchievementCount`, so the unified `UnlockContext` is fully populated
  // (not a partial) and the variant grid + achievement cards share one
  // facts-set. (audit §9.4)
  const currentWeekDays = state?.dailyStreak?.currentWeekDays || 0
  const longestTotalStreak = state?.streak?.longestTotalStreak || 0
  const totalDailyGoalsReached = state?.dailyStreak?.totalDailyGoalsReached || 0
  const totalPerfectSessions = state?.dailyStreak?.totalPerfectSessions || 0

  return useMemo(() => {
    const ctx: UnlockContext = {
      totalQuestionsAnswered,
      masteredCount,
      consecutiveFullWeeks,
      level,
      xp,
      currentWeekDays,
      longestTotalStreak,
      totalDailyGoalsReached,
      totalPerfectSessions,
      totalModules: MODULES.length,
      hasDiscoveredLongPress,
    }

    const variants: VariantUnlockInfo[] = VARIANT_DEFINITIONS.map((def) => {
      const isUnlocked = def.check(ctx)
      return {
        id: def.id,
        name: def.hiddenName && !isUnlocked ? def.hiddenName : def.name,
        unlocked: isUnlocked,
        condition: def.condition,
        rarity: def.rarity,
        description:
          def.hiddenDescription && !isUnlocked
            ? def.hiddenDescription
            : def.description,
        progress: def.getProgress(ctx),
      }
    })

    const unlockedCount = variants.filter((v) => v.unlocked).length

    return { variants, unlockedCount }
  }, [
    level,
    xp,
    masteredCount,
    totalQuestionsAnswered,
    consecutiveFullWeeks,
    currentWeekDays,
    longestTotalStreak,
    totalDailyGoalsReached,
    totalPerfectSessions,
    hasDiscoveredLongPress,
  ])
}

/** Total number of variants available */
export const TOTAL_VARIANTS = VARIANT_DEFINITIONS.length
